import Button from 'components/Button';
import BaseModal from 'components/Modals/BaseModal/BaseModal';
import ActivityType from 'core/enums/activityType';
import { TaskMediaType } from 'core/enums/taskMediaType';
import { TaskPriority } from 'core/enums/taskPriority';
import {
    TaskSubject,
    TaskSubjectWithApplication,
    TaskSubjectWithoutApplication,
    TaskSubjectWithoutContact,
} from 'core/enums/taskSubject';
import { useApplicationById, useApplications } from 'core/api/applications';
import { useContactById, useContacts, useInvalidateContacts } from 'core/api/contacts';
import { useAddTaskMutation, useUpdateTaskMutation } from 'core/api/tasks';
import Task from 'core/models/task';
import { getApplicationLabel } from 'core/useCases/applications/utils';
import { getTaskMediaOptionsForContact } from 'core/useCases/contacts/utils';
import { DisabledField, ModalType } from 'core/useCases/modal/types';
import { FC, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useModalStore } from 'stores/modal';
import { SSpacedColumn } from 'styles/layout';
import { enumKeys } from 'utils/common';
import { computeDateWithoutTime } from 'utils/dateTimeFormat';
import { DateOrTimeInput, SelectInput, TextInput } from '../lib';
import ResourcesInput from '../lib/ResourcesInput';
import { OptionsProps } from '../lib/SelectInput';

import { logEvent } from 'config/tracking';
import useResponsive from 'hooks/responsive';
import { TrackingEventName } from 'utils/types/amplitude';
import EventType from 'core/enums/eventType';
import styled from 'styled-components';

type Inputs = {
    applicationId: string;
    subject?: TaskSubject;
    customSubject?: string;
    contactId?: string;
    mediaType?: TaskMediaType;
    deadline: Date;
    priority: TaskPriority;
    comments?: string;
};

interface TaskFormProps {
    closeModal(): void;
    defaultValues?: Partial<Task>;
    disabledFields?: Array<DisabledField>;
    cancelButtonLabel?: string;
    allowSwitchToEvent?: boolean;
}

const SSwitchToEventLink = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.color.secondary.main};
    cursor: pointer;
    font-weight: bold;
    margin-right: auto;
    text-decoration: underline;
`;

const computeData = (task?: Partial<Task>): Partial<Inputs> | undefined => {
    if (!task) {
        return undefined;
    }
     
    const { contact, id, deadline, application, ...data } = task;
    const computedData = {
        ...data,
        applicationId: application?.id,
        contactId: contact?.id,
        deadline: deadline ? new Date(deadline) : undefined,
    } as Partial<Inputs>;

    return computedData;
};

const TaskForm: FC<TaskFormProps> = ({ closeModal, defaultValues, disabledFields, cancelButtonLabel, allowSwitchToEvent }) => {
    const { t } = useTranslation();
    const openModal = useModalStore((s) => s.openModal);
    const closeModalStore = useModalStore((s) => s.closeModal);
    const invalidateContacts = useInvalidateContacts();
    const addTaskMutation = useAddTaskMutation();
    const updateTaskMutation = useUpdateTaskMutation();
    const { isMobile } = useResponsive();
    const [contactOptions, setContactOptions] = useState<OptionsProps>([]);
    const [taskSubjectOptions, setTaskSubjectOptions] = useState<OptionsProps>([]);
    const [mediaTypeOptions, setMediaTypeOptions] = useState<OptionsProps>([]);
    const [taskPriorityOptions, setTaskPriorityOptions] = useState<OptionsProps>([]);
    const { data: contactsData } = useContacts();
    const contacts = useMemo(() => contactsData ?? [], [contactsData]);
    const { data: applicationsData } = useApplications();
    const applicationOptions = useMemo(
        () =>
            (applicationsData ?? []).map((application) => ({
                label: getApplicationLabel(application, t),
                value: application.id,
            })),
        [applicationsData, t],
    );

    const methods = useForm<Inputs>({
        defaultValues: computeData(defaultValues),
    });
    const { handleSubmit, control, watch, setValue } = methods;
    const selectedSubject = watch('subject');
    const selectedContactId = watch('contactId');
    const selectedApplicationId = watch('applicationId');
    const selectedContact = useContactById(selectedContactId).data;
    const selectedApplication = useApplicationById(selectedApplicationId).data;

    const isFirstTaskContext = defaultValues?.subject === TaskSubject.FirstContact;

    const openEventModal = () => {
        openModal({
            cancelButtonLabel: t('common.ignore'),
            defaultValues: {
                application: defaultValues?.application,
                contact: defaultValues?.contact,
                type: EventType.Interview,
            },
            disabledFields,
            title: isFirstTaskContext
                ? t('task-list.add-first-event')
                : t('task.new-event-modal-title'),
            type: ModalType.Event,
        });
    };

    const onSubmit = handleSubmit(async (values: Inputs) => {
        const computeValues = { ...values, deadline: computeDateWithoutTime(values.deadline) };
        try {
            if (defaultValues?.id) {
                // Update
                await updateTaskMutation.mutateAsync({ id: defaultValues.id, inputs: computeValues });
                closeModalStore();
            } else {
                // Creation
                const newTask = await addTaskMutation.mutateAsync(computeValues);
                logEvent({
                    name: TrackingEventName.TASK_CREATED,
                    params: { subject: newTask.subject, taskId: newTask.id },
                });
                closeModalStore();

                if (new Date(newTask.deadline) < new Date()) {
                    openModal({
                        defaultValues: {
                            activityType: ActivityType.Task,
                            id: newTask.id,
                            isAlreadyCompleted: false,
                        },
                        title: t('common.mark-as-complete'),
                        type: ModalType.ActivityReport,
                    });
                }
            }

            // TODO: for contact deletion button recheck, fetch only updated data
            await invalidateContacts();

            toast.success(t(`task-form.${defaultValues?.id ? 'update' : 'creation'}.success`));
        } catch (e) {
            toast.error(t(`task-form.${defaultValues?.id ? 'update' : 'creation'}.error`));
        }
    });

    useEffect(() => {
        const taskPriorityOptionsComputed: OptionsProps = [];
        for (const value of enumKeys(TaskPriority)) {
            taskPriorityOptionsComputed.push({
                label: t(`common.task-priority.enum.${TaskPriority[value]}`),
                value: TaskPriority[value],
            });
        }

        setTaskPriorityOptions(taskPriorityOptionsComputed);
    }, [setTaskPriorityOptions]);

    useEffect(() => {
        // If subject selected is a task subject without contact, reset the contact
        if (selectedSubject && TaskSubjectWithoutContact.includes(selectedSubject)) {
            setValue('contactId', undefined);
        }
    }, [selectedSubject, setValue]);

    // Refresh subject options and contact options according to application selected or not
    useEffect(() => {
        const listOfSubject = selectedApplication ? TaskSubjectWithApplication : TaskSubjectWithoutApplication;

        if (defaultValues?.subject && !listOfSubject.includes(defaultValues.subject)) {
            listOfSubject.push(defaultValues.subject);
        }

        setTaskSubjectOptions(
            listOfSubject.map((subject) => ({
                label: t(`common.task-subject.enum.${subject}`),
                value: subject,
            })),
        );

        if (selectedSubject && !listOfSubject.includes(selectedSubject)) {
            setValue('subject', undefined);
        }

        const contactsComputed = contacts.filter(
            (contact) => !selectedApplication || selectedApplication.contacts.map((c) => c.id).includes(contact.id),
        );

        const contactIdsComputed = contactsComputed.map((contact) => contact.id);

        setContactOptions(
            contactsComputed.map((contact) => ({
                label: `${contact.firstName} ${contact.lastName}`,
                value: contact.id,
            })),
        );

        // If selected application have changed with a value, reset contact
        // because the contact should be in the list of application linked contact
        if (selectedApplication && selectedContactId && !contactIdsComputed.includes(selectedContactId)) {
            setValue('contactId', undefined);
        }
    }, [setTaskSubjectOptions, selectedApplication, setValue, selectedSubject, selectedContactId, contacts]);

    // Refresh of media type options according to selected contact
    useEffect(() => {
        if (selectedContact) {
            setMediaTypeOptions(getTaskMediaOptionsForContact(selectedContact, t));
        } else {
            // If unselect contact, reset mediatype selected and reset the list of choices
            setMediaTypeOptions([]);
            setValue('mediaType', undefined);
        }
    }, [setMediaTypeOptions, selectedContact, setValue]);

    return (
        <FormProvider {...methods}>
            <form onSubmit={onSubmit}>
                <SelectInput
                    control={control}
                    isDisabled={disabledFields && disabledFields.includes(DisabledField.ApplicationId)}
                    label={t('application.label')}
                    name="applicationId"
                    options={applicationOptions}
                    semi={!isMobile}
                />
                <SSpacedColumn>
                    <SelectInput
                        control={control}
                        isDisabled={disabledFields && disabledFields.includes(DisabledField.Subject)}
                        label={t('common.subject.label')}
                        name="subject"
                        options={taskSubjectOptions}
                        semi={!isMobile}
                        required
                    />
                    {selectedSubject === 'other' && (
                        <TextInput
                            label={t('common.customSubject.label')}
                            name="customSubject"
                            semi={!isMobile}
                            required
                        />
                    )}
                </SSpacedColumn>
                {(!selectedSubject || !TaskSubjectWithoutContact.includes(selectedSubject)) && (
                    <SSpacedColumn>
                        <SelectInput
                            control={control}
                            isDisabled={disabledFields && disabledFields.includes(DisabledField.ContactId)}
                            label={t('common.contact')}
                            name="contactId"
                            options={contactOptions}
                            placeholder={t('common.select-field-placeholder')}
                            // Field not required if task linked to an application
                            required={!selectedApplicationId}
                            semi={!isMobile}
                        />
                        {selectedContactId && (
                            <SelectInput
                                control={control}
                                label={t('common.media-type.label')}
                                name="mediaType"
                                options={mediaTypeOptions}
                                placeholder={t('common.select-field-placeholder')}
                                // Field not required if task linked to an application
                                semi={!isMobile}
                                required
                            />
                        )}
                    </SSpacedColumn>
                )}
                <SSpacedColumn>
                    <DateOrTimeInput
                        control={control}
                        label={t('common.end-date')}
                        name="deadline"
                        placeholder={t('common.select-field-placeholder')}
                        semi={!isMobile}
                        required
                    />
                    <SelectInput
                        control={control}
                        label={t('common.task-priority.short-label')}
                        name="priority"
                        options={taskPriorityOptions}
                        placeholder={t('common.select-field-placeholder')}
                        semi={!isMobile}
                        required
                    />
                </SSpacedColumn>
                <TextInput
                    label={t('common.comment')}
                    name="comments"
                    placeholder={t('common.write-a-comment')}
                    isTextArea
                />
                <ResourcesInput name="resources" />
                <BaseModal.BottomActions>
                    {allowSwitchToEvent && (
                        <SSwitchToEventLink onClick={openEventModal} type="button">
                            {isFirstTaskContext
                                ? t('task-list.switch-to-first-event')
                                : t('task.switch-to-next-event')}
                        </SSwitchToEventLink>
                    )}
                    <Button onClick={() => closeModal()} variant="text">
                        {cancelButtonLabel || t('common.cancel')}
                    </Button>
                    <Button submit>{defaultValues?.id ? t('common.update') : t('common.create')}</Button>
                </BaseModal.BottomActions>
            </form>
        </FormProvider>
    );
};

export default TaskForm;
